import { useState } from "react";
import { ArrowUpRight, Check, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = { name: "", email: "", organisation: "", interest: "institutions", message: "" };

const inputCls =
    "w-full border border-white/15 bg-transparent px-4 py-3.5 text-sm text-white placeholder:text-slate/70 transition-colors duration-300 focus:border-sky focus:outline-none";
const labelCls = "mb-2 block text-[10px] uppercase tracking-[0.25em] text-sky/50";

export default function EnquiryForm() {
    const [form, setForm] = useState(EMPTY);
    const [status, setStatus] = useState("idle");
    const [error, setError] = useState("");

    const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        setStatus("sending");
        setError("");
        try {
            const res = await fetch(`${API}/enquiries`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(form),
            });
            if (!res.ok) throw new Error("Request failed");
            setStatus("sent");
        } catch {
            setStatus("error");
            setError("Transmission interrupted — please email us directly instead.");
        }
    };

    if (status === "sent") {
        return (
            <div className="mt-8 border border-sky/40 bg-white/[0.03] p-8 md:p-10" data-testid="enquiry-success">
                <span className="grid h-10 w-10 place-items-center border border-sky text-sky">
                    <Check className="h-4 w-4" />
                </span>
                <p className="mt-6 font-display text-2xl font-semibold tracking-tight text-white">
                    Enquiry received.
                </p>
                <p className="mt-3 max-w-sm break-all text-sm leading-relaxed text-sky/70">
                    Thank you. The QRUS team will respond to {form.email} shortly.
                </p>
                <button
                    data-testid="enquiry-send-another"
                    onClick={() => {
                        setForm(EMPTY);
                        setStatus("idle");
                    }}
                    className="mt-8 border border-white/20 px-6 py-3 text-xs font-medium uppercase tracking-[0.2em] text-white transition-colors duration-300 hover:border-sky hover:text-sky"
                >
                    Send another
                </button>
            </div>
        );
    }

    return (
        <form onSubmit={submit} className="mt-8" data-testid="enquiry-form">
            <div className="grid grid-cols-2 gap-px border border-white/15 bg-white/15">
                {[
                    { v: "institutions", label: "For Institutions" },
                    { v: "students", label: "For Students" },
                ].map((opt) => (
                    <button
                        type="button"
                        key={opt.v}
                        data-testid={`enquiry-interest-${opt.v}`}
                        onClick={() => setForm((f) => ({ ...f, interest: opt.v }))}
                        aria-pressed={form.interest === opt.v}
                        className={`px-4 py-3.5 text-xs font-medium uppercase tracking-[0.2em] transition-colors duration-300 ${
                            form.interest === opt.v
                                ? "bg-sky text-navy"
                                : "bg-navy text-slate hover:text-white"
                        }`}
                    >
                        {opt.label}
                    </button>
                ))}
            </div>

            <div className="mt-6 grid gap-5 sm:grid-cols-2">
                <div>
                    <label htmlFor="enquiry-name" className={labelCls}>Name *</label>
                    <input
                        id="enquiry-name"
                        data-testid="enquiry-name-input"
                        required
                        value={form.name}
                        onChange={set("name")}
                        placeholder="Your full name"
                        className={inputCls}
                    />
                </div>
                <div>
                    <label htmlFor="enquiry-email" className={labelCls}>Email *</label>
                    <input
                        id="enquiry-email"
                        data-testid="enquiry-email-input"
                        type="email"
                        required
                        value={form.email}
                        onChange={set("email")}
                        placeholder="you@example.com"
                        className={inputCls}
                    />
                </div>
            </div>

            <div className="mt-5">
                <label htmlFor="enquiry-org" className={labelCls}>Organisation</label>
                <input
                    id="enquiry-org"
                    data-testid="enquiry-org-input"
                    value={form.organisation}
                    onChange={set("organisation")}
                    placeholder="University / College / Company"
                    className={inputCls}
                />
            </div>

            <div className="mt-5">
                <label htmlFor="enquiry-message" className={labelCls}>Message *</label>
                <textarea
                    id="enquiry-message"
                    data-testid="enquiry-message-input"
                    required
                    rows={4}
                    value={form.message}
                    onChange={set("message")}
                    placeholder="Tell us what you're building towards."
                    className={`${inputCls} resize-none`}
                />
            </div>

            {status === "error" && (
                <p data-testid="enquiry-error" className="mt-4 text-sm text-sky">
                    {error}
                </p>
            )}

            <button
                data-testid="enquiry-submit-button"
                type="submit"
                disabled={status === "sending"}
                className="group mt-8 flex w-full items-center justify-center gap-2.5 bg-sky px-8 py-4 text-sm font-medium tracking-wide text-navy transition-colors duration-300 hover:bg-white disabled:opacity-60 sm:w-auto"
            >
                {status === "sending" ? (
                    <>
                        Transmitting
                        <Loader2 className="h-4 w-4 animate-spin" />
                    </>
                ) : (
                    <>
                        Send enquiry
                        <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </>
                )}
            </button>
        </form>
    );
}
